import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import api from '../services/api';

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
}

interface Collaborator {
  user: User;
  status: 'pending' | 'accepted' | 'declined';
}

interface Project {
  _id: string;
  name: string;
  designer: User;
  clients: User[];
  collaborators?: Collaborator[];
  location?: { latitude: number; longitude: number };
  trackingEnabled?: boolean;
  managementEnabled?: boolean;
  createdAt: string;
}

interface Pin {
  _id: string;
  location: { latitude: number; longitude: number };
  note: string;
  media?: { contentType: string; url: string };
  createdBy: string;
  createdAt: string;
}

interface CourseElement {
  _id: string;
  category: string;
  mediaUrl?: string;
  caption?: string;
  createdBy: string;
  createdAt: string;
}

const ProjectViewScreen: React.FC = () => {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const { user, token } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<Project | null>(null);
  const [pins, setPins] = useState<Pin[]>([]);
  const [elements, setElements] = useState<CourseElement[]>([]);

  // Media preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState<'image' | 'video' | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Delete operations
  const [deletingElementId, setDeletingElementId] = useState<string | null>(null);
  const [deletingPinId, setDeletingPinId] = useState<string | null>(null);

  // Load project details
  const loadProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  }, [projectId]);

  // Load pins
  const loadPins = useCallback(async (): Promise<Pin[]> => {
    if (!projectId) return [];
    try {
      const response = await api.get(`/pins/${projectId}`);
      const pinsData = Array.isArray(response.data) ? response.data : [];
      setPins(pinsData);
      return pinsData;
    } catch (error) {
      console.error('Failed to load pins:', error);
      return [];
    }
  }, [projectId]);

  // Load course elements
  const loadElements = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/course-elements/${projectId}`);
      const elementsData = Array.isArray(response.data) ? response.data : [];
      setElements(elementsData);
    } catch (error) {
      console.error('Failed to load elements:', error);
    }
  }, [projectId]);



  // Load all data
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      try {
        await loadProject();
        await loadElements();
        await loadPins();
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [loadProject, loadElements, loadPins]);

  // Open media preview (like mobile app - download first, then show locally)
  const openPreview = async (title: string, mediaUrl: string) => {
    try {
      setPreviewTitle(title);
      setPreviewLoading(true);
      setShowPreviewModal(true);

      // Construct absolute URL like mobile app
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const absUrl = mediaUrl.startsWith('http')
        ? mediaUrl
        : `${baseURL}${mediaUrl.startsWith('/') ? mediaUrl : `/${mediaUrl}`}`;

      // Download with auth (like mobile app does)
      const response = await fetch(absUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to download media: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Determine type from blob content type or URL
      const contentType = response.headers.get('content-type') || '';
      if (contentType.startsWith('video/')) {
        setPreviewType('video');
      } else if (contentType.startsWith('image/')) {
        setPreviewType('image');
      } else {
        // Fallback based on extension
        if (absUrl.match(/\.(mp4|mov|m4v|webm|avi)(\?.*)?$/i)) {
          setPreviewType('video');
        } else {
          setPreviewType('image');
        }
      }

      setPreviewUrl(blobUrl);

    } catch (error) {
      console.error('Preview failed:', error);
      alert('Failed to load media for preview');
      setShowPreviewModal(false);
      setPreviewType(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Delete course element
  const deleteElement = async (elementId: string) => {
    if (!window.confirm('Delete this course element? This action cannot be undone.')) return;

    try {
      setDeletingElementId(elementId);
      await api.delete(`/course-elements/${elementId}`);
      setElements(prev => prev.filter(el => el._id !== elementId));
    } catch (error) {
      console.error('Failed to delete element:', error);
      alert('Failed to delete course element');
    } finally {
      setDeletingElementId(null);
    }
  };

  // Delete pin
  const deletePin = async (pinId: string) => {
    if (!window.confirm('Delete this pin? This will also delete any associated media.')) return;

    try {
      setDeletingPinId(pinId);
      await api.delete(`/pins/${pinId}`);
      setPins(prev => prev.filter(pin => pin._id !== pinId));
    } catch (error) {
      console.error('Failed to delete pin:', error);
      alert('Failed to delete pin');
    } finally {
      setDeletingPinId(null);
    }
  };

  // Permission checks
  const canDeleteElement = (element: CourseElement) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (user.role === 'designer' && project?.designer._id === user.id) return true;
    return false;
  };

  const canDeletePin = (pin: Pin) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (user.role === 'designer' && project?.designer._id === user.id) return true;
    if (user.role === 'client' && pin.createdBy === user.id) return true;
    return false;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const closePreview = () => {
    // Clean up blob URL on close
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setShowPreviewModal(false);
    setPreviewUrl('');
    setPreviewType(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-lg">Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-red-600 text-lg">Project not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">{project.name}</h1>
          <div className="flex items-center space-x-3">
            {project.trackingEnabled && (
              <button
                onClick={() => navigate(`/project/${projectId}/progress`)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                View Progress
              </button>
            )}
            {project.managementEnabled && (user?.role === 'admin' || user?.role === 'designer') && (
              <button
                onClick={() => navigate(`/project/${projectId}/management`)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Manage Phases
              </button>
            )}
            <button
              onClick={() => navigate(`/map/${projectId}`)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Open Map
            </button>
            {/* Hide "Back to Projects" button for clients */}
            {(user?.role === 'designer' || user?.role === 'admin') && (
              <button
                onClick={() => navigate('/projects')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Back to Projects
              </button>
            )}
          </div>
        </div>

        {/* Project Details */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Project Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Designer</h3>
              <div className="bg-gray-50 p-3 rounded">
                <div className="font-medium">{project.designer.name}</div>
                <div className="text-sm text-gray-600">{project.designer.email}</div>
              </div>
              {(project.collaborators ?? []).filter(c => c.status === 'accepted').length > 0 && (
                <div className="mt-3">
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Collaborating Designers</h4>
                  <div className="space-y-1">
                    {(project.collaborators ?? [])
                      .filter(c => c.status === 'accepted')
                      .map(c => (
                        <div key={c.user._id} className="bg-indigo-50 p-2 rounded flex items-center space-x-2">
                          <span className="inline-block w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
                          <div>
                            <div className="font-medium text-sm">{c.user.name}</div>
                            <div className="text-xs text-gray-600">{c.user.email}</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <h3 className="font-medium text-gray-700 mb-2">
                Clients ({project.clients.length})
              </h3>
              <div className="space-y-2">
                {project.clients.length === 0 ? (
                  <div className="text-gray-500 italic">No clients assigned</div>
                ) : (
                  project.clients.map(client => (
                    <div key={client._id} className="bg-gray-50 p-2 rounded">
                      <div className="font-medium text-sm">{client.name}</div>
                      <div className="text-xs text-gray-600">{client.email}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Created: {formatDate(project.createdAt)}
          </div>
        </div>

        {/* Course Elements */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold">Course Elements</h2>
              <p className="text-sm text-gray-600">Media grouped by category</p>
            </div>
            {(user?.role === 'admin' || user?.role === 'designer' || user?.role === 'client') && (
              <button
                onClick={() => navigate(`/course-elements/${projectId}`)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                {elements.length === 0 ? 'Upload Course Elements' : 'Add More Elements'}
              </button>
            )}
          </div>

          {elements.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500">No course elements uploaded yet</div>
            </div>
          ) : (
            <div className="space-y-3">
              {elements.map(element => (
                <div key={element._id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-blue-600">{element.category}</div>
                      {element.caption && (
                        <div className="text-gray-700 mt-1">{element.caption}</div>
                      )}
                      <div className="text-sm text-gray-500 mt-1">
                        {formatDate(element.createdAt)}
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => openPreview(
                          `${element.category} - ${formatDate(element.createdAt)}`,
                          element.mediaUrl || `/course-elements/${element._id}/media`
                        )}
                        className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                      >
                        Preview
                      </button>
                      {canDeleteElement(element) && (
                        <button
                          onClick={() => deleteElement(element._id)}
                          disabled={deletingElementId === element._id}
                          className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 disabled:opacity-50"
                        >
                          {deletingElementId === element._id ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pins */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Map Pins</h2>
          <p className="text-sm text-gray-600 mb-4">All site notes and media</p>

          {pins.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500">No pins placed yet</div>
              {(user?.role === 'admin' || user?.role === 'designer' || user?.role === 'client') && (
                <button
                  onClick={() => navigate(`/map/${projectId}`)}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Add Pins on Map
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {pins.map(pin => {
                const hasMedia = !!pin.media?.url;
                const lat = pin.location.latitude.toFixed(6);
                const lng = pin.location.longitude.toFixed(6);

                return (
                  <div key={pin._id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium">Pin at {lat}, {lng}</div>
                        {pin.note && (
                          <div className="text-gray-700 mt-1">{pin.note}</div>
                        )}
                        <div className="text-sm text-gray-500 mt-1">
                          {formatDate(pin.createdAt)}
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        {hasMedia && (
                          <button
                            onClick={() => openPreview(
                              `Pin ${pin._id.slice(-5)} @ ${lat}, ${lng}`,
                              `/pins/${pin._id}/media`
                            )}
                            className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                          >
                            Preview
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/map/${projectId}`, {
                            state: { focusPinId: pin._id }
                          })}
                          className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                        >
                          View on Map
                        </button>
                        {canDeletePin(pin) && (
                          <button
                            onClick={() => deletePin(pin._id)}
                            disabled={deletingPinId === pin._id}
                            className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 disabled:opacity-50"
                          >
                            {deletingPinId === pin._id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Media Preview Modal */}
      {showPreviewModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
          onClick={closePreview}
        >
          <div className="max-w-4xl max-h-[90vh] w-full p-4">
            <button
              onClick={closePreview}
              className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-300"
            >
              ×
            </button>
            {previewLoading ? (
              <div className="flex items-center justify-center py-12 text-white">
                <div className="text-lg">Loading media...</div>
              </div>
            ) : (
              <>
                <div className="mb-4 text-white">
                  <h3 className="text-lg font-semibold">{previewTitle}</h3>
                </div>
                <div className="flex justify-center">
                  {previewType === 'image' ? (
                    <img
                      src={previewUrl}
                      alt={previewTitle}
                      className="max-w-full max-h-[75vh] object-contain"
                      onError={() => {
                        alert('Failed to load image');
                        closePreview();
                      }}
                    />
                  ) : previewType === 'video' ? (
                    <video
                      src={previewUrl}
                      controls
                      className="max-w-full max-h-[75vh]"
                      onError={() => {
                        alert('Failed to load video');
                        closePreview();
                      }}
                    >
                      Your browser does not support video playback.
                    </video>
                  ) : (
                    <div className="text-white">Unable to preview this media type</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectViewScreen;
